export type TLanguageDef = {
  id: number;
  code: string;
  name: string;
  native: string;
};

const SPECIAL_ENGLISH_LANGUAGE_NAMES: { [key: string]: string } = {
  cn: 'Simplified Chinese',
  pt: 'Brazilian Portuguese'
};

const ENGLISH_LANGUAGE_DISPLAY_NAMES =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl ? new Intl.DisplayNames(['en'], { type: 'language' }) : null;

export function getEnglishLanguageName(language: Pick<TLanguageDef, 'code' | 'name' | 'native'>) {
  const specialName = SPECIAL_ENGLISH_LANGUAGE_NAMES[language.code];
  if (specialName) {
    return specialName;
  }

  const name = language.name.trim();
  const native = language.native.trim();
  const parenMatch = name.match(/\(([^()]+)\)\s*$/);

  if (parenMatch) {
    return parenMatch[1].trim();
  }

  if (name !== native) {
    return name;
  }

  try {
    const normalizedCode = language.code === 'cn' ? 'zh' : language.code;
    return ENGLISH_LANGUAGE_DISPLAY_NAMES?.of(normalizedCode) || name;
  } catch {
    return name;
  }
}

export function formatLanguageLabel(language: Pick<TLanguageDef, 'code' | 'name' | 'native'>) {
  const englishName = getEnglishLanguageName(language);

  return englishName !== language.native ? `${language.native} (${englishName})` : englishName;
}

export const LANGUAGES: TLanguageDef[] = [
  {
    id: 1,
    code: 'en',
    name: 'English',
    native: 'English'
  },
  {
    id: 2,
    code: 'de',
    name: 'German',
    native: 'Deutsch'
  },
  {
    id: 3,
    code: 'es',
    name: 'Spanish',
    native: 'Español'
  },
  {
    id: 4,
    code: 'fr',
    name: 'French',
    native: 'Français'
  },
  {
    id: 5,
    code: 'it',
    name: 'Italian',
    native: 'Italiano'
  },
  {
    id: 6,
    code: 'pt',
    name: 'Portuguese',
    native: 'Português'
  },
  {
    id: 7,
    code: 'ru',
    name: 'Russian',
    native: 'Русский'
  },
  {
    id: 8,
    code: 'ko',
    name: 'Korean',
    native: '한국어'
  },
  {
    id: 9,
    code: 'cn',
    name: 'Simplified Chinese',
    native: '简体中文'
  },
  {
    id: 10,
    code: 'aa',
    name: 'Afar',
    native: 'Afar'
  },
  {
    id: 11,
    code: 'af',
    name: 'Afrikaans',
    native: 'Afrikaans'
  },
  {
    id: 12,
    code: 'ak',
    name: 'Akan',
    native: 'Akana'
  },
  {
    id: 13,
    code: 'an',
    name: 'Aragonese',
    native: 'Aragonés'
  },
  {
    id: 14,
    code: 'gn',
    name: 'Guarani',
    native: "Avañe'ẽ"
  },
  {
    id: 15,
    code: 'ay',
    name: 'Aymara',
    native: 'Aymar'
  },
  {
    id: 16,
    code: 'az',
    name: 'Azerbaijani',
    native: 'Azərbaycanca / آذربايجان'
  },
  {
    id: 17,
    code: 'id',
    name: 'Indonesian',
    native: 'Bahasa Indonesia'
  },
  {
    id: 18,
    code: 'ms',
    name: 'Malay',
    native: 'Bahasa Melayu'
  },
  {
    id: 19,
    code: 'bm',
    name: 'Bambara',
    native: 'Bamanankan'
  },
  {
    id: 20,
    code: 'jv',
    name: 'Javanese',
    native: 'Basa Jawa'
  },
  {
    id: 21,
    code: 'su',
    name: 'Sundanese',
    native: 'Basa Sunda'
  },
  {
    id: 22,
    code: 'mt',
    name: 'Maltese',
    native: 'bil-Malti'
  },
  {
    id: 23,
    code: 'bi',
    name: 'Bislama',
    native: 'Bislama'
  },
  {
    id: 24,
    code: 'bs',
    name: 'Bosnian',
    native: 'Bosanski'
  },
  {
    id: 25,
    code: 'br',
    name: 'Breton',
    native: 'Brezhoneg'
  },
  {
    id: 26,
    code: 'ca',
    name: 'Catalan',
    native: 'Català'
  },
  {
    id: 27,
    code: 'cs',
    name: 'Czech',
    native: 'Česky'
  },
  {
    id: 28,
    code: 'ch',
    name: 'Chamorro',
    native: 'Chamoru'
  },
  {
    id: 29,
    code: 'ny',
    name: 'Chichewa',
    native: 'Chi-Chewa'
  },
  {
    id: 30,
    code: 'sn',
    name: 'Shona',
    native: 'chiShona'
  },
  {
    id: 31,
    code: 'co',
    name: 'Corsican',
    native: 'Corsu'
  },
  {
    id: 32,
    code: 'za',
    name: 'Zhuang',
    native: 'Cuengh / Tôô / 壮语'
  },
  {
    id: 33,
    code: 'cy',
    name: 'Welsh',
    native: 'Cymraeg'
  },
  {
    id: 34,
    code: 'da',
    name: 'Danish',
    native: 'Dansk'
  },
  {
    id: 35,
    code: 'nv',
    name: 'Navajo',
    native: 'Diné bizaad'
  },
  {
    id: 36,
    code: 'na',
    name: 'Nauruan',
    native: 'Dorerin Naoero'
  },
  {
    id: 37,
    code: 'et',
    name: 'Estonian',
    native: 'Eesti'
  },
  {
    id: 38,
    code: 'eo',
    name: 'Esperanto',
    native: 'Esperanto'
  },
  {
    id: 39,
    code: 'eu',
    name: 'Basque',
    native: 'Euskara'
  },
  {
    id: 40,
    code: 'ee',
    name: 'Ewe',
    native: 'Ɛʋɛ'
  },
  {
    id: 41,
    code: 'fo',
    name: 'Faroese',
    native: 'Føroyskt'
  },
  {
    id: 42,
    code: 'fy',
    name: 'West Frisian',
    native: 'Frysk'
  },
  {
    id: 43,
    code: 'ff',
    name: 'Peul',
    native: 'Fulfulde'
  },
  {
    id: 44,
    code: 'ga',
    name: 'Irish',
    native: 'Gaeilge'
  },
  {
    id: 45,
    code: 'gv',
    name: 'Manx',
    native: 'Gaelg'
  },
  {
    id: 46,
    code: 'sm',
    name: 'Samoan',
    native: 'Gagana Samoa'
  },
  {
    id: 47,
    code: 'gd',
    name: 'Scottish Gaelic',
    native: 'Gàidhlig'
  },
  {
    id: 48,
    code: 'gl',
    name: 'Galician',
    native: 'Galego'
  },
  {
    id: 49,
    code: 'ki',
    name: 'Kikuyu',
    native: 'Gĩkũyũ'
  },
  {
    id: 50,
    code: 'ho',
    name: 'Hiri Motu',
    native: 'Hiri Motu'
  },
  {
    id: 51,
    code: 'hr',
    name: 'Croatian',
    native: 'Hrvatski'
  },
  {
    id: 52,
    code: 'io',
    name: 'Ido',
    native: 'Ido'
  },
  {
    id: 53,
    code: 'ig',
    name: 'Igbo',
    native: 'Igbo'
  },
  {
    id: 54,
    code: 'ia',
    name: 'Interlingua',
    native: 'Interlingua'
  },
  {
    id: 55,
    code: 'ie',
    name: 'Interlingue',
    native: 'Interlingue'
  },
  {
    id: 56,
    code: 'ik',
    name: 'Inupiak',
    native: 'Iñupiak'
  },
  {
    id: 57,
    code: 'nr',
    name: 'South Ndebele',
    native: 'isiNdebele'
  },
  {
    id: 58,
    code: 'xh',
    name: 'Xhosa',
    native: 'isiXhosa'
  },
  {
    id: 59,
    code: 'zu',
    name: 'Zulu',
    native: 'isiZulu'
  },
  {
    id: 60,
    code: 'is',
    name: 'Icelandic',
    native: 'Íslenska'
  },
  {
    id: 61,
    code: 'mh',
    name: 'Marshallese',
    native: 'Kajin Majel / Ebon'
  },
  {
    id: 62,
    code: 'kl',
    name: 'Greenlandic',
    native: 'Kalaallisut'
  },
  {
    id: 63,
    code: 'kr',
    name: 'Kanuri',
    native: 'Kanuri'
  },
  {
    id: 64,
    code: 'kw',
    name: 'Cornish',
    native: 'Kernewek'
  },
  {
    id: 65,
    code: 'kg',
    name: 'Kongo',
    native: 'KiKongo'
  },
  {
    id: 66,
    code: 'rw',
    name: 'Rwandi',
    native: 'Kinyarwandi'
  },
  {
    id: 67,
    code: 'rn',
    name: 'Kirundi',
    native: 'Kirundi'
  },
  {
    id: 68,
    code: 'sw',
    name: 'Swahili',
    native: 'Kiswahili'
  },
  {
    id: 69,
    code: 'ky',
    name: 'Kirghiz',
    native: 'Kırgızca / Кыргызча'
  },
  {
    id: 70,
    code: 'ht',
    name: 'Haitian',
    native: 'Krèyol ayisyen'
  },
  {
    id: 71,
    code: 'kj',
    name: 'Kuanyama',
    native: 'Kuanyama'
  },
  {
    id: 72,
    code: 'ku',
    name: 'Kurdish',
    native: 'Kurdî / كوردی'
  },
  {
    id: 73,
    code: 'la',
    name: 'Latin',
    native: 'Latina'
  },
  {
    id: 74,
    code: 'lv',
    name: 'Latvian',
    native: 'Latviešu'
  },
  {
    id: 75,
    code: 'to',
    name: 'Tonga',
    native: 'Lea Faka-Tonga'
  },
  {
    id: 76,
    code: 'lb',
    name: 'Luxembourgish',
    native: 'Lëtzebuergesch'
  },
  {
    id: 77,
    code: 'lt',
    name: 'Lithuanian',
    native: 'Lietuvių'
  },
  {
    id: 78,
    code: 'li',
    name: 'Limburgian',
    native: 'Limburgs'
  },
  {
    id: 79,
    code: 'ln',
    name: 'Lingala',
    native: 'Lingála'
  },
  {
    id: 80,
    code: 'lg',
    name: 'Ganda',
    native: 'Luganda'
  },
  {
    id: 81,
    code: 'hu',
    name: 'Hungarian',
    native: 'Magyar'
  },
  {
    id: 82,
    code: 'mg',
    name: 'Malagasy',
    native: 'Malagasy'
  },
  {
    id: 83,
    code: 'mi',
    name: 'Maori',
    native: 'Māori'
  },
  {
    id: 84,
    code: 'mo',
    name: 'Moldovan',
    native: 'Moldovenească'
  },
  {
    id: 85,
    code: 'fj',
    name: 'Fijian',
    native: 'Na Vosa Vakaviti'
  },
  {
    id: 86,
    code: 'nl',
    name: 'Dutch',
    native: 'Nederlands'
  },
  {
    id: 87,
    code: 'cr',
    name: 'Cree',
    native: 'Nehiyaw'
  },
  {
    id: 88,
    code: 'no',
    name: 'Norwegian',
    native: 'Norsk'
  },
  {
    id: 89,
    code: 'nb',
    name: 'Norwegian Bokmål',
    native: 'Norsk bokmål'
  },
  {
    id: 90,
    code: 'nn',
    name: 'Norwegian Nynorsk',
    native: 'Norsk nynorsk'
  },
  {
    id: 91,
    code: 'oc',
    name: 'Occitan',
    native: 'Occitan'
  },
  {
    id: 92,
    code: 'om',
    name: 'Oromo',
    native: 'Oromoo'
  },
  {
    id: 93,
    code: 'ng',
    name: 'Ndonga',
    native: 'Oshiwambo'
  },
  {
    id: 94,
    code: 'hz',
    name: 'Herero',
    native: 'Otsiherero'
  },
  {
    id: 95,
    code: 'pi',
    name: 'Pali',
    native: 'Pāli / पाऴि'
  },
  {
    id: 96,
    code: 'pl',
    name: 'Polish',
    native: 'Polski'
  },
  {
    id: 97,
    code: 'ty',
    name: 'Tahitian',
    native: 'Reo Mā`ohi'
  },
  {
    id: 98,
    code: 'ro',
    name: 'Romanian',
    native: 'Română'
  },
  {
    id: 99,
    code: 'rm',
    name: 'Raeto Romance',
    native: 'Rumantsch'
  },
  {
    id: 100,
    code: 'qu',
    name: 'Quechua',
    native: 'Runa Simi'
  },
  {
    id: 101,
    code: 'se',
    name: 'Northern Sami',
    native: 'Sámegiella'
  },
  {
    id: 102,
    code: 'sg',
    name: 'Sango',
    native: 'Sängö'
  },
  {
    id: 103,
    code: 'sc',
    name: 'Sardinian',
    native: 'Sardu'
  },
  {
    id: 104,
    code: 'st',
    name: 'Southern Sotho',
    native: 'Sesotho'
  },
  {
    id: 105,
    code: 'tn',
    name: 'Tswana',
    native: 'Setswana'
  },
  {
    id: 106,
    code: 'sq',
    name: 'Albanian',
    native: 'Shqip'
  },
  {
    id: 107,
    code: 'nd',
    name: 'North Ndebele',
    native: 'Sindebele'
  },
  {
    id: 108,
    code: 'ss',
    name: 'Swati',
    native: 'SiSwati'
  },
  {
    id: 109,
    code: 'sk',
    name: 'Slovak',
    native: 'Slovenčina'
  },
  {
    id: 110,
    code: 'sl',
    name: 'Slovenian',
    native: 'Slovenščina'
  },
  {
    id: 111,
    code: 'so',
    name: 'Somalia',
    native: 'Soomaaliga'
  },
  {
    id: 112,
    code: 'sh',
    name: 'Serbo-Croatian',
    native: 'Srpskohrvatski / Српскохрватски'
  },
  {
    id: 113,
    code: 'fi',
    name: 'Finnish',
    native: 'Suomi'
  },
  {
    id: 114,
    code: 'sv',
    name: 'Swedish',
    native: 'Svenska'
  },
  {
    id: 115,
    code: 'tl',
    name: 'Tagalog / Filipino',
    native: 'Tagalog'
  },
  {
    id: 116,
    code: 'tt',
    name: 'Tatar',
    native: 'Tatarça'
  },
  {
    id: 117,
    code: 'vi',
    name: 'Vietnamese',
    native: 'Tiếng Việt'
  },
  {
    id: 118,
    code: 'lu',
    name: 'Luba-Katanga',
    native: 'Tshiluba'
  },
  {
    id: 119,
    code: 've',
    name: 'Venda',
    native: 'Tshivenḓa'
  },
  {
    id: 120,
    code: 'tr',
    name: 'Turkish',
    native: 'Türkçe'
  },
  {
    id: 121,
    code: 'tw',
    name: 'Twi',
    native: 'Twi'
  },
  {
    id: 122,
    code: 'ug',
    name: 'Uyghur',
    native: 'Uyƣurqə / ئۇيغۇرچە'
  },
  {
    id: 123,
    code: 'vo',
    name: 'Volapük',
    native: 'Volapük'
  },
  {
    id: 124,
    code: 'wa',
    name: 'Walloon',
    native: 'Walon'
  },
  {
    id: 125,
    code: 'wo',
    name: 'Wolof',
    native: 'Wollof'
  },
  {
    id: 126,
    code: 'ts',
    name: 'Tsonga',
    native: 'Xitsonga'
  },
  {
    id: 127,
    code: 'yo',
    name: 'Yoruba',
    native: 'Yorùbá'
  },
  {
    id: 128,
    code: 'el',
    name: 'Greek',
    native: 'Ελληνικά'
  },
  {
    id: 129,
    code: 'av',
    name: 'Avar',
    native: 'Авар'
  },
  {
    id: 130,
    code: 'ab',
    name: 'Abkhazian',
    native: 'Аҧсуа'
  },
  {
    id: 131,
    code: 'ba',
    name: 'Bashkir',
    native: 'Башҡорт'
  },
  {
    id: 132,
    code: 'be',
    name: 'Belarusian',
    native: 'Беларуская'
  },
  {
    id: 133,
    code: 'bg',
    name: 'Bulgarian',
    native: 'Български'
  },
  {
    id: 134,
    code: 'os',
    name: 'Ossetian / Ossetic',
    native: 'Иронау'
  },
  {
    id: 135,
    code: 'kv',
    name: 'Komi',
    native: 'Коми'
  },
  {
    id: 136,
    code: 'kk',
    name: 'Kazakh',
    native: 'Қазақша'
  },
  {
    id: 137,
    code: 'mk',
    name: 'Macedonian',
    native: 'Македонски'
  },
  {
    id: 138,
    code: 'mn',
    name: 'Mongolian',
    native: 'Монгол'
  },
  {
    id: 139,
    code: 'ce',
    name: 'Chechen',
    native: 'Нохчийн'
  },
  {
    id: 140,
    code: 'cu',
    name: 'Old Church Slavonic / Old Bulgarian',
    native: 'словѣньскъ / slověnĭskŭ'
  },
  {
    id: 141,
    code: 'sr',
    name: 'Serbian',
    native: 'Српски'
  },
  {
    id: 142,
    code: 'tg',
    name: 'Tajik',
    native: 'Тоҷикӣ'
  },
  {
    id: 143,
    code: 'tk',
    name: 'Turkmen',
    native: 'Туркмен / تركمن'
  },
  {
    id: 144,
    code: 'uz',
    name: 'Uzbek',
    native: 'Ўзбек'
  },
  {
    id: 145,
    code: 'uk',
    name: 'Ukrainian',
    native: 'Українська'
  },
  {
    id: 146,
    code: 'cv',
    name: 'Chuvash',
    native: 'Чăваш'
  },
  {
    id: 147,
    code: 'ka',
    name: 'Georgian',
    native: 'ქართული'
  },
  {
    id: 148,
    code: 'hy',
    name: 'Armenian',
    native: 'Հայերեն'
  },
  {
    id: 149,
    code: 'yi',
    name: 'Yiddish',
    native: 'ייִדיש'
  },
  {
    id: 150,
    code: 'he',
    name: 'Hebrew',
    native: 'עברית'
  },
  {
    id: 151,
    code: 'ur',
    name: 'Urdu',
    native: 'اردو'
  },
  {
    id: 152,
    code: 'ar',
    name: 'Arabic',
    native: 'العربية'
  },
  {
    id: 153,
    code: 'ps',
    name: 'Pashto',
    native: 'پښتو'
  },
  {
    id: 154,
    code: 'fa',
    name: 'Persian',
    native: 'فارسی'
  },
  {
    id: 155,
    code: 'ha',
    name: 'Hausa',
    native: 'هَوُسَ'
  },
  {
    id: 156,
    code: 'dv',
    name: 'Divehi',
    native: 'ދިވެހިބަސް'
  },
  {
    id: 157,
    code: 'ti',
    name: 'Tigrinya',
    native: 'ትግርኛ'
  },
  {
    id: 158,
    code: 'am',
    name: 'Amharic',
    native: 'አማርኛ'
  },
  {
    id: 159,
    code: 'ks',
    name: 'Kashmiri',
    native: 'कश्मीरी / كشميري'
  },
  {
    id: 160,
    code: 'ne',
    name: 'Nepali',
    native: 'नेपाली'
  },
  {
    id: 161,
    code: 'bh',
    name: 'Bihari',
    native: 'भोजपुरी'
  },
  {
    id: 162,
    code: 'mr',
    name: 'Marathi',
    native: 'मराठी'
  },
  {
    id: 163,
    code: 'sa',
    name: 'Sanskrit',
    native: 'संस्कृतम्'
  },
  {
    id: 164,
    code: 'sd',
    name: 'Sindhi',
    native: 'सिनधि'
  },
  {
    id: 165,
    code: 'hi',
    name: 'Hindi',
    native: 'हिन्दी'
  },
  {
    id: 166,
    code: 'as',
    name: 'Assamese',
    native: 'অসমীয়া'
  },
  {
    id: 167,
    code: 'bn',
    name: 'Bengali',
    native: 'বাংলা'
  },
  {
    id: 168,
    code: 'pa',
    name: 'Panjabi / Punjabi',
    native: 'ਪੰਜਾਬੀ / पंजाबी / پنجابي'
  },
  {
    id: 169,
    code: 'gu',
    name: 'Gujarati',
    native: 'ગુજરાતી'
  },
  {
    id: 170,
    code: 'or',
    name: 'Oriya',
    native: 'ଓଡ଼ିଆ'
  },
  {
    id: 171,
    code: 'ta',
    name: 'Tamil',
    native: 'தமிழ்'
  },
  {
    id: 172,
    code: 'te',
    name: 'Telugu',
    native: 'తెలుగు'
  },
  {
    id: 173,
    code: 'kn',
    name: 'Kannada',
    native: 'ಕನ್ನಡ'
  },
  {
    id: 174,
    code: 'ml',
    name: 'Malayalam',
    native: 'മലയാളം'
  },
  {
    id: 175,
    code: 'si',
    name: 'Sinhalese',
    native: 'සිංහල'
  },
  {
    id: 176,
    code: 'th',
    name: 'Thai',
    native: 'ไทย / Phasa Thai'
  },
  {
    id: 177,
    code: 'lo',
    name: 'Laotian',
    native: 'ລາວ / Pha xa lao'
  },
  {
    id: 178,
    code: 'dz',
    name: 'Dzongkha',
    native: 'ཇོང་ཁ'
  },
  {
    id: 179,
    code: 'bo',
    name: 'Tibetan',
    native: 'བོད་ཡིག / Bod skad'
  },
  {
    id: 180,
    code: 'my',
    name: 'Burmese',
    native: 'မြန်မာစာ'
  },
  {
    id: 181,
    code: 'km',
    name: 'Cambodian',
    native: 'ភាសាខ្មែរ'
  },
  {
    id: 182,
    code: 'iu',
    name: 'Inuktitut',
    native: 'ᐃᓄᒃᑎᑐᑦ'
  },
  {
    id: 183,
    code: 'oj',
    name: 'Ojibwa',
    native: 'ᐊᓂᔑᓈᐯᒧᐎᓐ / Anishinaabemowin'
  },
  {
    id: 184,
    code: 'ii',
    name: 'Sichuan Yi',
    native: 'ꆇꉙ / 四川彝语'
  },
  {
    id: 185,
    code: 'zh',
    name: 'Chinese',
    native: '中文'
  },
  {
    id: 186,
    code: 'ja',
    name: 'Japanese',
    native: '日本語'
  },
  {
    id: 187,
    code: 'sgn',
    name: 'Sign Language',
    native: 'Sign Language'
  }
];

type TLanguageDefDictionary = { [key: string]: TLanguageDef };

export const LANGUAGES_BY_CODE: TLanguageDefDictionary = LANGUAGES.reduce((accum, item) => {
  accum[item.code] = item;
  return accum;
}, {} as TLanguageDefDictionary);

export const LANGUAGES_BY_ID: TLanguageDefDictionary = LANGUAGES.reduce((accum, item) => {
  accum[item.id] = item;
  return accum;
}, {} as TLanguageDefDictionary);
